"use client";

import { useEffect, useState } from "react";
import { useRouter } from "next/navigation";
import { EventType, trackEventWithCommonProps } from "@utils/eventTracker";
import { Input } from "@components/atoms/Input";
import styles from "./CarValuationRegistrationWidget.module.scss";
import { CAR_VALUATION_SEO_PATH } from "@constants/carValuationSeo";
import { CarValuationSeoRecentItem } from "@interfaces/carValuationSeo.types";
import { PAGE_TYPES } from "@constants/pageTypes";
import { useIsMobile } from "@hooks/use-mobile";
import Image from "next/image";
import assetLoader from "@utils/internalImageLoder";
import { useGlobalLoader } from "@hooks/useGlobalLoader";
import { cn } from "@utils/utils";
import { registrationNumberValidator, sanitizeRegistrationNumber } from "@utils/registrationNumberFormat";

interface CarValuationRegistrationWidgetProps {
  ctaLabel: string;
  placeholder: string;
  recentSearches: CarValuationSeoRecentItem[];
  isLoggedIn: boolean;
  phone?: string;
  pageType?: string;
  pagePath?: string;
  journey?: string;
  stickyTriggerId?: string;
  initialIsMobile?: boolean;
}

export default function CarValuationRegistrationWidget({
  ctaLabel,
  placeholder,
  recentSearches,
  isLoggedIn,
  phone,
  pageType = PAGE_TYPES.VALUATION_SEO_PAGE,
  pagePath = CAR_VALUATION_SEO_PATH,
  journey = "used_car_v2",
  stickyTriggerId,
  initialIsMobile = false,
}: CarValuationRegistrationWidgetProps) {
  const router = useRouter();
  const [registrationNumber, setRegistrationNumber] = useState("");
  const [error, setError] = useState("");
  const [showStickyCta, setShowStickyCta] = useState(false);
  const isMobile = useIsMobile(initialIsMobile);
  const { showLoader, hideLoader } = useGlobalLoader();
  const normalizedRegistrationNumber = sanitizeRegistrationNumber(registrationNumber);
  const isRegistrationValid = registrationNumberValidator(registrationNumber);

  useEffect(() => {
    if (!isMobile) {
      setShowStickyCta(false);
      return;
    }

    const targetElement = stickyTriggerId
      ? document.getElementById(stickyTriggerId)
      : null;

    if (!targetElement || typeof IntersectionObserver === "undefined") {
      const onScroll = () => {
        setShowStickyCta(window.scrollY > 260);
      };

      onScroll();
      window.addEventListener("scroll", onScroll, { passive: true });
      return () => window.removeEventListener("scroll", onScroll);
    }

    const observer = new IntersectionObserver(
      ([entry]) => {
        setShowStickyCta(!entry.isIntersecting);
      },
      {
        threshold: 0.05,
      }
    );

    observer.observe(targetElement);

    return () => observer.disconnect();
  }, [isMobile, stickyTriggerId]);

  const redirectToCarInformation = (value: string) => {
    router.push(
      `/v2/car-valuation/car-information?reg_no=${encodeURIComponent(value)}`
    );
  };

  const submitRegistration = async () => {
    showLoader()
    const finalRegistrationNumber = normalizedRegistrationNumber;

    if (!isRegistrationValid) {
      hideLoader();
      setError("Enter a valid car registration number");
      await trackEventWithCommonProps("exception", EventType.TRACK_EVENT, {
        pageType,
        additionalProps: {
          exception_type: "validation_error",
          exception_name: "invalid_car_registration_number",
          journey,
          logged_in: isLoggedIn ? "yes" : "no",
          phone,
          registration_number: finalRegistrationNumber,
        },
      });
      return;
    }

    setError("");
    setRegistrationNumber(finalRegistrationNumber);

    await trackEventWithCommonProps("action", EventType.TRACK_EVENT, {
      pageType,
      additionalProps: {
        action_type: "tap_btn",
        action_name: "tap_btn_submit_registration_number",
        journey,
        logged_in: isLoggedIn ? "yes" : "no",
        phone,
        registration_number: finalRegistrationNumber,
        saved_assets: "no",
        url: `${window.location.origin}${pagePath}`,
      },
    });

    redirectToCarInformation(finalRegistrationNumber);
  };

  const submitRecentSearch = async (item: CarValuationSeoRecentItem) => {
    showLoader();
    setError("");
    const finalRegistrationNumber = sanitizeRegistrationNumber(item.registrationNumber);

    await trackEventWithCommonProps("action", EventType.TRACK_EVENT, {
      pageType,
      additionalProps: {
        action_type: "tap_btn",
        action_name:
          item.source === "recent"
            ? "tap_btn_recent_valuations"
            : "tap_btn_submit_saved_asset",
        journey,
        logged_in: isLoggedIn ? "yes" : "no",
        phone,
        registration_number: finalRegistrationNumber,
        saved_assets: "yes",
      },
    });

    redirectToCarInformation(finalRegistrationNumber);
  };

  const renderCountryBadge = () => (
    <div className={styles.countryBadge} aria-hidden="true">
      <Image
        src={`${assetLoader()}/icons/ind.webp`}
        alt="ind"
        fill
        className={styles.countryBadgeImage}
        priority
      />
    </div>
  );

  return (
    <>
      <div className={styles.widget}>
        <div className={styles.inputRow}>
          <div className={styles.registrationFieldWrap}>
            {renderCountryBadge()}
            <Input
              value={registrationNumber}
              onChange={(event) => {
                setRegistrationNumber(event.target.value.toLocaleUpperCase());
                if (error) setError("");
              }}
              onKeyDown={(event) => {
                if (event.key === "Enter") {
                  event.preventDefault();
                  submitRegistration();
                }
              }}
              placeholder={placeholder}
              className={styles.input}
              error={error}
              maxLength={12}
              inputMode="text"
              autoCapitalize="characters"
              aria-label="Car registration number"
            />
          </div>
          {!isMobile && ctaLabel ? (
            <button
              type="button"
              className={styles.primaryButton}
              onClick={submitRegistration}
            >
              {ctaLabel}
            </button>
          ) : null}
        </div>

        {recentSearches.length > 0 && (
          <div className={styles.recentChips}>
            {recentSearches?.map((item) => (
              <button
                key={item.id}
                type="button"
                className={styles.recentChip}
                onClick={() => submitRecentSearch(item)}
              >
                <Image
                  src={item?.imageUrl || `${assetLoader()}/images/valuationFallbackCar.webp`}
                  alt={item.label}
                  width={56}
                  height={32}
                  className={styles.recentChipImage}
                  onError={(e) => {
                    (e.target as HTMLImageElement).src = `${assetLoader()}/images/valuationFallbackCar.webp`;
                  }}
                />
                <div className={styles.recentChipContent}>
                  <span className={styles.recentChipPlate}>{item.label}</span>
                  <span className={styles.recentChipName}>
                    {item.vehicleName || "Used car"}
                  </span>
                </div>
              </button>
            ))}
          </div>
        )}
        {isMobile && ctaLabel ? (
          <button
            type="button"
            className={styles.primaryButton}
            onClick={submitRegistration}
          >
            {ctaLabel}
          </button>
        ) : null}
      </div>

      {showStickyCta && ctaLabel ? (
        <div className={styles.stickyCta}>
          <div className={styles.stickyCtaRow}>
            <div className={styles.stickyCtaInputWrap}>
              <div className={styles.registrationFieldWrap}>
                {renderCountryBadge()}
                <Input
                  value={registrationNumber}
                  onChange={(event) => {
                    setRegistrationNumber(event.target.value.toLocaleUpperCase());
                    if (error) setError("");
                  }}
                  onKeyDown={(event) => {
                    if (event.key === "Enter") {
                      event.preventDefault();
                      submitRegistration();
                    }
                  }}
                  placeholder={placeholder}
                  className={cn(styles.stickyCtaInput, error && styles.stickyCtaInputError)}
                  maxLength={12}
                  inputMode="text"
                  autoCapitalize="characters"
                  aria-label="Car registration number"
                />
              </div>
            </div>
            <button
              type="button"
              className={styles.stickyCtaButton}
              onClick={submitRegistration}
            >
              {ctaLabel}
            </button>
          </div>
          {error ? <p className={styles.stickyCtaError}>{error}</p> : null}
        </div>
      ) : null}
    </>
  );
}
